import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";

/** Modal that lets the admin pick an existing gallery photo to insert into a blog post. */
export default function GalleryPickerDialog({ onClose, onSelect }) {
  const [query, setQuery] = useState("");
  const { data: photos = [], isLoading } = useQuery({
    queryKey: ["adminGalleryPicker"],
    queryFn: async () => (await api.get("/admin/gallery/photos")).data,
  });
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return photos;
    return photos.filter((p) =>
      [p.caption, p.alt_text, p.class_title, (p.tags || []).join(" ")]
        .filter(Boolean).some((s) => s.toLowerCase().includes(q))
    );
  }, [photos, query]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gg-surface max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Insert from Gallery</DialogTitle>
          <p className="text-sm text-gg-muted">Click a photo to embed it in your post. Alt text is copied automatically.</p>
        </DialogHeader>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gg-muted" />
          <Input
            placeholder="Search by caption, alt text, tag, or class"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="gallery-picker-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gg-muted hover:text-gg-brown"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {isLoading ? (
            <div className="py-16 text-center text-gg-muted">Loading gallery…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gg-muted">
              {photos.length === 0 ? "No gallery photos uploaded yet." : "No photos match your search."}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pb-4">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelect({
                    url: `${API}${p.image_url}`,
                    alt_text: p.alt_text || p.caption || "",
                    caption: p.caption || "",
                  })}
                  className="group relative rounded-lg overflow-hidden border border-gg-border hover:border-gg-green hover:shadow-lg transition-all"
                  data-testid={`gallery-picker-photo-${p.id}`}
                >
                  <img
                    src={`${API}${p.image_url}`}
                    alt={p.alt_text || p.caption || ""}
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                  {(p.caption || p.class_title) && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                      <p className="text-xs text-white line-clamp-2">{p.caption || p.class_title}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
